import { ref } from "vue";

import { fetchJson } from "~/recipal-app/controllers/shared/requests";

export function useOpenLotCodeSearch({
  sourceUrl = "",
  seed = [],
  seedTotal = 0,
  searchParams = () => ({}),
  searchRemotely = false,
} = {}) {
  return sourceUrl && searchRemotely
    ? remoteSearch({ sourceUrl, seed, seedTotal, searchParams })
    : localSearch({ seed });
}

function localSearch({ seed }) {
  const results = ref(seed);
  const totalEntries = ref(seed.length);
  const loading = ref(false);

  function matches(lot, term) {
    return String(lot.lot_code ?? "")
      .toLowerCase()
      .includes(term);
  }

  function search(q) {
    const term = (q ?? "").trim().toLowerCase();
    results.value = term ? seed.filter((lot) => matches(lot, term)) : seed;
    totalEntries.value = results.value.length;
  }

  function clear() {
    results.value = [];
    totalEntries.value = 0;
  }

  return {
    remote: false,
    results,
    totalEntries,
    loading,
    search,
    showSeed: () => search(""),
    abort: () => {},
    clear,
  };
}

function remoteSearch({ sourceUrl, seed, seedTotal, searchParams }) {
  const results = ref(seed);
  const totalEntries = ref(seedTotal);
  const loading = ref(false);
  let abortController = null;

  function abort() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  async function search(q, { page = 1, perPage = 30 } = {}) {
    abort();
    const controller = new AbortController();
    abortController = controller;

    loading.value = true;

    try {
      const params = new URLSearchParams({
        ...searchParams(),
        q: q ?? "",
        per_page: perPage,
        page,
      });
      const { response, data } = await fetchJson(`${sourceUrl}?${params}`, {
        signal: controller.signal,
      });

      if (!response.ok || !data) {
        throw new Error(`Open lot code search failed: ${response.status}`);
      }

      results.value = data.open_lot_codes;
      totalEntries.value = data.total_entries;
    } catch (e) {
      if (e.name !== "AbortError") throw e;
    } finally {
      // A superseded request must not clear the loading flag a newer one just set.
      if (abortController === controller) {
        abortController = null;
        loading.value = false;
      }
    }
  }

  function showSeed() {
    if (!seed.length) return search("");

    abort();
    loading.value = false;
    results.value = seed;
    totalEntries.value = seedTotal;
  }

  function clear() {
    abort();
    loading.value = false;
    results.value = [];
    totalEntries.value = 0;
  }

  return { remote: true, results, totalEntries, loading, search, showSeed, abort, clear };
}
